import React from "react";
import { Box, Typography, Button, Paper, Grid, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { isNullUndEmpty } from "utils/general";
import { envSettings } from "core/EnvSettings";

const useSlotStyles = makeStyles({
	'@keyframes flashRed': {
		'0%, 100%': {
			backgroundColor: 'transparent',
			borderColor: 'rgba(0, 0, 0, 0.12)',
		},
		'25%, 75%': {
			backgroundColor: 'rgba(244, 67, 54, 0.2)',
			borderColor: '#f44336',
		},
		'50%': {
			backgroundColor: 'transparent',
			borderColor: 'rgba(0, 0, 0, 0.12)',
		},
	},
	slotContainer: {
		padding: '12px 8px',
		border: '2px solid rgba(0, 0, 0, 0.12)',
		borderRadius: 4,
		transition: 'all 0.3s ease',
		minHeight: 70,
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
	},
	slotSuccess: {
		backgroundColor: 'rgba(76, 175, 80, 0.1)',
		borderColor: '#4caf50',
	},
	slotError: {
		animation: '$flashRed 0.6s ease-in-out 2',
	},
	slotNeutral: {
		backgroundColor: 'transparent',
		borderColor: 'rgba(0, 0, 0, 0.12)',
	},
});

const Slot = ({ label, value, validationState = 'neutral' }) => {
	const classes = useSlotStyles();
	const isEmpty = isNullUndEmpty(value);

	const getSlotClass = () => {
		if (validationState === 'success') return classes.slotSuccess;
		if (validationState === 'error') return classes.slotError;
		return classes.slotNeutral;
	};

	return (
		<Box className={`${classes.slotContainer} ${getSlotClass()}`}>
			<Typography variant="caption" color="textSecondary" style={{ marginBottom: 4 }}>
				{label}
			</Typography>
			<Typography
				variant="body2"
				style={{
					fontWeight: 500,
					wordBreak: "break-all",
					overflowWrap: "break-word",
					color: isEmpty ? "gray" : "inherit",
					lineHeight: 1.4,
				}}
			>
				{isEmpty ? "—" : value}
			</Typography>
		</Box>
	);
};

const getMessageColor = (lastMessage) => {
	if (!lastMessage) return 'inherit';
	if (lastMessage.type === 'success') return '#4caf50';
	if (lastMessage.type === 'error') return '#f44336';
	if (lastMessage.type === 'warning') return '#ff9800';
	return 'inherit';
};

export const ValidationState = {
	neutral: "neutral",
	success: "success",
	error: "error",
};

export const StoreByScanner = ({
	translate,
	scannedPallet = null,
	scannedStorage = null,
	stackedPallets = [],
	onClear,
	palletValidationState = ValidationState.neutral,
	storageValidationState = ValidationState.neutral,
	suggestedStoragePlace = null,
	allowClear = true,
	lastMessage = null,
	isLoading = false,
}) => {
	// Use code from scanned object, fall back to suggestedStoragePlace for storage
	const palletCode = stackedPallets.length
		? [scannedPallet?.code, ...stackedPallets.map((p) => p.code)].join(", ")
		: scannedPallet?.code;
	const storageCode = scannedStorage?.code ?? suggestedStoragePlace;

	const handleClear = () => {
		allowClear && onClear && onClear();
	};

	return (
		<Paper style={{ padding: 16, minWidth: 450 }}>
			<Grid container spacing={2} alignItems="stretch">
				<Grid item xs={12} sm={allowClear ? 4 : 6}>
					<Slot
						label={translate("tab.pallets.pallet")}
						value={palletCode}
						validationState={palletValidationState}
					/>
				</Grid>
				<Grid item xs={12} sm={allowClear ? 4 : 6}>
					<Slot
						label={translate(envSettings.REACT_APP_FF_STORAGE_MODULE ? "tab.pallets.storage_place" : "tab.pallets.area_id")}
						value={storageCode}
						validationState={storageValidationState}
					/>
				</Grid>
				{allowClear ?
				<Grid item xs={12} sm={4} style={{ display: 'flex', alignItems: 'stretch' }}>
					<Button
						variant="outlined"
						color="secondary"
						fullWidth
						onClick={handleClear}
						disabled={!(palletCode || storageCode)}
						style={{ minHeight: 70, fontSize: '0.875rem', padding: '6px 12px' }}
					>
						{translate("tab.pallets.clear")}
					</Button>
				</Grid>
				: null}
			</Grid>
			{(lastMessage || isLoading) && (
				<Box mt={1} display="flex" justifyContent="center" alignItems="center">
					{isLoading && (
						<CircularProgress size={16} style={{ marginRight: 8, color: '#1976d2' }} />
					)}
					{lastMessage && (
						<Typography
							variant="body2"
							style={{
								color: getMessageColor(lastMessage),
								fontWeight: 500,
								textAlign: 'center',
							}}
						>
							{lastMessage.text}
						</Typography>
					)}
				</Box>
			)}
		</Paper>
	);
};
